import { useState, useEffect } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import toast from "react-hot-toast";
import { useCurrency } from "../../utils";
import { getCostosDeLote, crearCostosLote, actualizarCostoLote, eliminarCostoLote } from "../../utils/loteCostos";

// Componentes de costo (landed cost) de un lote de compra (botón por fila en el tab Lotes, 9.36).
// El FOB vive en lotes_compra (costo_unitario × cantidad_total) y es la BASE del lote: acá NO se edita,
// solo se muestra de referencia. Este modal maneja los componentes ADICIONALES (transporte / aduana /
// otro) que suman —no particionan— al valor. Cada componente lleva su propia moneda y fecha_pago.
// Fase A commit 2: solo CARGA. La proyección CapEx y la conciliación contra banco son commits posteriores;
// el total que se muestra es REFERENCIAL (la capitalización real al activo llega después). Guardado
// SELECTIVO: nunca toca componentes pagados (blindaje; hoy no existen aún).

const HOY = new Date().toISOString().split("T")[0];
const esPagado = (c) => c.estado === "pagado";

const normalizarFecha = (f) => (f && String(f).includes("T") ? String(f).substring(0, 10) : f || "");

const TIPOS_COMPONENTE = [
  { value: "transporte", label: "Transporte" },
  { value: "aduana", label: "Aduana" },
  { value: "otro", label: "Otro" },
];

const MONEDAS_POR_PAIS = {
  Chile: ["CLP", "USD", "UF"],
  Perú: ["PEN", "USD"],
};

const LoteCostosModal = ({ show, onHide, lote, userId, onSaved }) => {
  const { convert, formatAmount, loadingRates } = useCurrency();

  const [cargando, setCargando] = useState(true);
  const [costos, setCostos] = useState([]);
  const [costosOriginales, setCostosOriginales] = useState([]);
  const [guardando, setGuardando] = useState(false);

  // Al abrir: precarga los componentes existentes del lote. El modal se monta fresco en cada apertura
  // (render condicional en el padre), así que basta un efecto de montaje.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const data = await getCostosDeLote(lote.id);
        if (!vivo) return;
        const normalizados = data.map((c) => ({
          ...c,
          monto: Number(c.monto),
          fecha_pago: normalizarFecha(c.fecha_pago),
        }));
        setCostos(normalizados);
        setCostosOriginales(normalizados);
      } catch (e) {
        toast.error(`Error al cargar los componentes: ${e.message || e}`);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const localMoneda = lote.pais === "Chile" ? "CLP" : "PEN";
  const monedasDisponibles = MONEDAS_POR_PAIS[lote.pais] || [localMoneda];

  // FOB = base del lote (costo_unitario × cantidad_total), en la moneda del lote. No editable acá.
  const fob = Number(lote.costo_unitario || 0) * Number(lote.cantidad_total || 0);

  // Total de referencia = FOB + Σ componentes, todo convertido a moneda local con el converter existente
  // (USD/UF/PEN ↔ local). REFERENCIAL: la capitalización real al valor del activo es un commit posterior.
  const fobLocal = convert(fob, lote.moneda, localMoneda);
  const componentesLocal = costos.reduce((s, c) => s + convert(Number(c.monto || 0), c.moneda, localMoneda), 0);
  const totalReferencia = fobLocal + componentesLocal;

  const puedeGuardar = !cargando && !guardando;

  const handleCambiar = (idx, field, value) => {
    setCostos((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const handleAgregarFila = () => {
    setCostos((prev) => [
      ...prev,
      { tipo: "transporte", monto: 0, moneda: localMoneda, fecha_pago: HOY, estado: "pendiente" },
    ]);
  };

  const handleQuitarFila = (idx) => {
    setCostos((prev) => prev.filter((_, i) => i !== idx));
  };

  // Guardado SELECTIVO (espejo del guardado de PlanPagosActivoModal): UPDATE pendientes con cambios,
  // INSERT pendientes nuevas, DELETE pendientes quitadas. Los pagados NUNCA se tocan (candado en el util).
  // Sin validación "suma == total": los componentes suman al valor, no particionan un monto.
  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const pendientesState = costos.filter((c) => !esPagado(c));
      const originalesPendientes = costosOriginales.filter((c) => !esPagado(c));
      const ops = [];

      // UPDATE: pendientes con id, solo si cambió algún campo editable.
      for (const c of pendientesState) {
        if (!c.id) continue;
        const orig = costosOriginales.find((o) => o.id === c.id);
        if (!orig) continue;
        const cambios = {};
        if (String(orig.tipo) !== String(c.tipo)) cambios.tipo = c.tipo;
        if (Number(orig.monto) !== Number(c.monto)) cambios.monto = Number(c.monto);
        if (String(orig.moneda) !== String(c.moneda)) cambios.moneda = c.moneda;
        if (String(orig.fecha_pago) !== String(c.fecha_pago)) cambios.fecha_pago = c.fecha_pago;
        if (Object.keys(cambios).length > 0) ops.push(actualizarCostoLote(c.id, cambios));
      }

      // INSERT: pendientes nuevas (sin id).
      const nuevas = pendientesState.filter((c) => !c.id);
      if (nuevas.length > 0) {
        ops.push(
          crearCostosLote(
            nuevas.map((c) => ({
              pais: lote.pais,
              lote_id: lote.id,
              tipo: c.tipo,
              monto: Number(c.monto),
              moneda: c.moneda,
              fecha_pago: c.fecha_pago,
              estado: "pendiente",
              created_by: userId,
            }))
          )
        );
      }

      // DELETE: pendientes que estaban en DB y el usuario quitó (guard en el util: solo pendientes).
      const idsEnState = new Set(pendientesState.filter((c) => c.id).map((c) => c.id));
      for (const o of originalesPendientes) {
        if (o.id && !idsEnState.has(o.id)) ops.push(eliminarCostoLote(o.id));
      }

      await Promise.all(ops);
      toast.success("Componentes de costo guardados");
      onSaved?.();
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Componentes de costo del lote</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 12 }}>
          <div>
            <strong>Descripción:</strong> {lote.descripcion || "—"}
          </div>
          <div>
            <strong>Cantidad:</strong> {lote.cantidad_total}
          </div>
          <div>
            <strong>FOB (base del lote):</strong> {formatAmount(fob, lote.moneda)} ({lote.moneda})
          </div>
          <div style={{ color: "#888", fontSize: "0.8rem" }}>
            El FOB es la base del lote y no se edita acá. Sumá los componentes adicionales (transporte, aduana, otro)
            que capitalizan al valor.
          </div>
        </div>

        {/* Tabla editable de componentes */}
        <Table size="sm" bordered style={{ fontSize: "0.85rem", marginBottom: 8 }}>
          <thead>
            <tr>
              <th style={{ width: 150 }}>Tipo</th>
              <th>Monto</th>
              <th style={{ width: 110 }}>Moneda</th>
              <th style={{ width: 170 }}>Fecha de pago</th>
              <th style={{ width: 44 }} />
            </tr>
          </thead>
          <tbody>
            {costos.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", color: "#888" }}>
                  Sin componentes. Agregá filas para sumar transporte / aduana / otros.
                </td>
              </tr>
            ) : (
              costos.map((c, idx) => {
                const bloqueado = esPagado(c);
                return (
                  <tr key={c.id || `nuevo-${idx}`}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={c.tipo}
                        onChange={(e) => handleCambiar(idx, "tipo", e.target.value)}
                        disabled={bloqueado || guardando}
                      >
                        {TIPOS_COMPONENTE.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={c.monto}
                        onChange={(e) => handleCambiar(idx, "monto", e.target.value)}
                        disabled={bloqueado || guardando}
                      />
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={c.moneda}
                        onChange={(e) => handleCambiar(idx, "moneda", e.target.value)}
                        disabled={bloqueado || guardando}
                      >
                        {monedasDisponibles.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control
                        type="date"
                        size="sm"
                        min={HOY}
                        value={normalizarFecha(c.fecha_pago)}
                        onChange={(e) => handleCambiar(idx, "fecha_pago", e.target.value)}
                        disabled={bloqueado || guardando}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {bloqueado ? (
                        <span style={{ color: "#888", fontSize: "0.75rem" }}>pagado</span>
                      ) : (
                        <Button
                          variant="link"
                          size="sm"
                          style={{ color: "#c62828", padding: 0, textDecoration: "none" }}
                          onClick={() => handleQuitarFila(idx)}
                          disabled={guardando}
                        >
                          ✕
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button variant="outline-secondary" size="sm" onClick={handleAgregarFila} disabled={cargando || guardando}>
            + Agregar componente
          </Button>
          <div style={{ fontSize: "0.9rem", fontWeight: 600, textAlign: "right" }}>
            <div style={{ color: "#333" }}>
              Total referencia (FOB + componentes):{" "}
              {loadingRates ? "…" : `${formatAmount(totalReferencia, localMoneda)} (${localMoneda})`}
            </div>
            <div style={{ fontWeight: 400, fontSize: "0.75rem", color: "#888" }}>
              Convertido a {localMoneda} con la cotización vigente. Referencial — la capitalización al activo llega en
              un commit posterior.
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={!puedeGuardar}>
          {guardando ? "Guardando…" : "Guardar componentes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LoteCostosModal;
